import React, { useState } from "react";
import { useApi } from "../../hooks/useApi.js";
import { sectionLabel } from "../../lib/sections.js";

/**
 * EngagementPanel — who is using this profile, and which parts.
 *
 * The one number the engagement work is judged by is "how much does Dawn use
 * this". This panel is that number, per caregiver, without a chart: visits in
 * the window, when she was last here, which sections she has opened (ten
 * dots, in section order), and whether she has watched the introduction.
 *
 * Reads GET /clients/:id/engagement?days=N (CAP_EDIT). Caregivers only —
 * coordinators' own previews are not engagement and are not shown (decision,
 * Sep 28). Every assigned caregiver appears, with zeros if she has never
 * opened it; "assigned and never opened" is the row that matters most.
 */

const SECTION_ORDER = [
  "meet", "communicate", "sensory", "passions", "day",
  "working_on", "hard_times", "health", "people", "community",
];
const SECTION_LABELS = {
  meet: "Meet", communicate: "Communicates", sensory: "Sensory",
  passions: "Passions", day: "Day", working_on: "Working on",
  hard_times: "Hard times", health: "Health", people: "People",
  community: "Community",
};
const WINDOWS = [7, 30, 90];

function relative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7)  return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function playWord(play) {
  if (!play?.started) return "not started";
  if (play.completed)  return "finished";
  return `partway (${Math.round((play.max_progress || 0) * 100)}%)`;
}

export default function EngagementPanel({ clientId, pronouns = "" }) {
  // "His day" / "Her day" / "Their day" from the person's pronouns (Oct 25).
  const nameOf = sec => (sec === "day" ? sectionLabel("day", pronouns, { sentence: true }) : SECTION_LABELS[sec]);
  const [days, setDays] = useState(30);
  const { data, loading, error } = useApi(`/clients/${clientId}/engagement?days=${days}`);
  const people = data?.people || [];
  // What caregivers took from it — their own sentences, newest first (B3).
  const { data: reflections } = useApi(`/clients/${clientId}/reflections?limit=10`);

  return (
    <div style={s.body}>
      <div style={s.head}>
        <span style={s.sub}>Visits counted in the last {days} days. Support staff only — a coordinator's own visits are not counted.</span>
        <div style={s.windows} role="group" aria-label="Window">
          {WINDOWS.map(w => (
            <button key={w} type="button" onClick={() => setDays(w)}
                    aria-pressed={days === w}
                    style={{ ...s.win, ...(days === w ? s.winOn : {}) }}>
              Last {w} days
            </button>
          ))}
        </div>
      </div>

      {error && <p style={s.error}>{error}</p>}
      {loading && !data && <p style={s.muted}>Loading…</p>}
      {data && people.length === 0 && (
        <p style={s.empty}>No DSPs are assigned to this profile yet.</p>
      )}

      {people.length > 0 && (
        <div style={s.list}>
          <div style={{ ...s.row, ...s.headerRow }} aria-hidden="true">
            <span style={s.name}>Support staff</span>
            <span style={s.visits}>Visits</span>
            <span style={s.last}>Last</span>
            <span style={s.dots}>Sections</span>
            <span style={s.play}>Introduction</span>
          </div>
          {people.map(p => (
            <div key={p.user_id} style={s.row}>
              <span style={s.name}>{p.name}</span>
              <span style={s.visits}>{p.visits}</span>
              <span style={s.last}>{relative(p.last_at)}</span>
              <span style={s.dots} aria-label={`Sections opened: ${p.sections_opened.map(nameOf).join(", ") || "none"}`}>
                {SECTION_ORDER.map(sec => {
                  const on = p.sections_opened.includes(sec);
                  return <i key={sec} title={`${nameOf(sec)} — ${on ? "opened" : "not opened yet"}`}
                            style={{ ...s.dot, ...(on ? s.dotOn : {}) }} />;
                })}
              </span>
              <span style={s.play}>{playWord(p.play)}</span>
            </div>
          ))}
        </div>
      )}
      {people.length > 0 && (
        <p style={s.legend}>
          <i style={{ ...s.dot, ...s.dotOn, verticalAlign: "middle" }} /> section opened in this window ·{" "}
          <i style={{ ...s.dot, verticalAlign: "middle" }} /> not yet — hover a dot for the section name.
        </p>
      )}
      {Array.isArray(data?.links) && data.links.length > 0 && (
        <div style={s.list}>
          {/* A link is not a person and records no events, but it knows how
              often it was opened and when (David, Oct 16). */}
          <div style={{ ...s.row, ...s.headerRow }} aria-hidden="true">
            <span style={s.name}>Visitors via link</span>
            <span style={s.visits}>Opens</span>
            <span style={s.last}>Last</span>
            <span style={s.dots}></span>
            <span style={s.play}></span>
          </div>
          {data.links.map(l => (
            <div key={l.link_id} style={{ ...s.row, ...(l.is_expired ? s.rowDim : {}) }}>
              <span style={s.name}>{l.label ? `“${l.label}”` : "Untitled link"}{l.is_expired ? " · expired" : ""}</span>
              <span style={s.visits}>{l.opens}</span>
              <span style={s.last}>{relative(l.last_at)}</span>
              <span style={s.dots}></span>
              <span style={s.play}>{l.asks_reflection ? "asks for a reflection" : ""}</span>
            </div>
          ))}
        </div>
      )}

      {data && people.some(p => p.visits === 0) && (
        <p style={s.muted}>A DSP with no visits has been given access but hasn't opened it.</p>
      )}

      {Array.isArray(data?.asked) && data.asked.length > 0 && (
        <div style={s.refl}>
          <h4 style={s.reflHead}>What people asked</h4>
          <p style={s.sub}>Questions typed into Ask in this window, and who asked. Questions with no answer yet come first — the profile had nothing confident for them, a gap worth filling.</p>
          <ul style={s.askedList}>
            {data.asked.map(a => {
              // Older payloads carry `answered` only.
              const unanswered = a.unanswered ?? !a.answered;
              const hasAskers = Array.isArray(a.askers) && a.askers.length > 0;
              return (
                <li key={a.q} style={s.askedRow}>
                  <span style={s.askedQ}>
                    “{a.q}”
                    {hasAskers && (
                      <span style={s.askedBy}> — {a.askers.join(", ")}</span>
                    )}
                  </span>
                  <span style={s.askedMeta}>
                    {/* With names, the names say how many people asked; a
                        repeat count would only count one person asking again
                        (David, Nov 1). Older payloads without names keep ×N. */}
                    {!hasAskers && a.count > 1 ? `×${a.count} · ` : ""}{relative(a.last_at)}
                    {unanswered && <span style={s.gap}>no answer yet</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {Array.isArray(reflections) && reflections.length > 0 && (
        <div style={s.refl}>
          <h4 style={s.reflHead}>What people took from it</h4>
          {reflections.map(r => (
            <blockquote key={r.id} style={s.quote}>
              <p style={s.quoteText}>“{r.text}”</p>
              <footer style={s.quoteMeta}>{r.visitor ? <em>{r.name}</em> : r.name} · {relative(r.created_at)}{r.after_ref && r.after_ref !== "full" ? ` · after ${r.after_ref.replace(/_/g, " ")}` : ""}</footer>
            </blockquote>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  body:      { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  sub:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  head:      { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap" },
  title:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  windows:   { display: "flex", gap: 4 },
  win:       { font: "inherit", fontSize: "var(--text-xs)", padding: "4px 10px", borderRadius: 999, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer" },
  winOn:     { border: "1px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-text-primary)" },
  muted:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", fontStyle: "italic", margin: 0 },
  empty:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", margin: 0 },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  list:      { display: "flex", flexDirection: "column", gap: 2 },
  row:       { display: "grid", gridTemplateColumns: "minmax(90px, 1.2fr) 52px 84px minmax(120px, 1fr) 110px", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--color-bg-base)", fontSize: "var(--text-sm)", color: "var(--color-text-primary)" },
  askedList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 },
  askedRow:  { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap", padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--color-bg-base)", fontSize: "var(--text-sm)" },
  askedQ:    { color: "var(--color-text-primary)" },
  askedBy:   { color: "var(--color-text-tertiary)" },
  askedMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", whiteSpace: "nowrap", display: "inline-flex", gap: 8, alignItems: "baseline" },
  gap:       { color: "var(--color-critical)", fontWeight: "var(--weight-medium)" },
  rowDim:     { opacity: 0.55 },
  headerRow: { background: "none", color: "var(--color-text-tertiary)", fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.06em" },
  name:      { fontWeight: "var(--weight-medium)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  visits:    { fontVariantNumeric: "tabular-nums" },
  last:      { color: "var(--color-text-secondary)" },
  dots:      { display: "flex", gap: 4 },
  dot:       { width: 9, height: 9, borderRadius: 999, border: "1px solid var(--color-border-strong)", background: "var(--color-surface)", display: "inline-block" },
  dotOn:     { border: "1px solid var(--color-accent)", background: "var(--color-accent)" },
  play:      { color: "var(--color-text-secondary)" },
  legend:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0 },
  refl:      { display: "flex", flexDirection: "column", gap: "var(--space-2)", paddingTop: "var(--space-3)", borderTop: "1px solid var(--color-border)" },
  reflHead:  { fontSize: "var(--text-xs)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-tertiary)", fontWeight: "var(--weight-semibold)", margin: 0 },
  quote:     { margin: 0, padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", borderRadius: "var(--radius-md)", borderLeft: "3px solid var(--color-accent-subtle)" },
  quoteText: { fontFamily: "var(--font-serif)", fontSize: "var(--text-sm)", color: "var(--color-text-primary)", margin: "0 0 2px", lineHeight: 1.45 },
  quoteMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
};
